import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = ({ transparent = false }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [hoverLink, setHoverLink] = useState(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Function to scroll to top when navigating
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Determine active link based on current path
  const getActiveLink = () => {
    const path = location.pathname;
    if (path === '/') return 'Strona Główna';
    if (path === '/uslugi') return 'Usługi';
    if (path === '/cennik') return 'Cennik';
    if (path === '/galeria') return 'Galeria';
    if (path === '/kontakt') return 'Kontakt';
    return 'Strona Główna'; // Default
  };

  const activeLink = hoverLink || getActiveLink();

  // Placeholder links - replace with actual routes if using React Router
  const navLinks = [
    { name: 'Strona Główna', href: '/' },
    { name: 'Usługi', href: '/uslugi' },
    { name: 'Cennik', href: '/cennik' },
    { name: 'Galeria', href: '/galeria' },
    { name: 'Kontakt', href: '/kontakt' },
  ];

  // Special case for Galeria page - always use sticky header with purple background
  const isGaleriaPage = location.pathname === '/galeria';
  
  const navClasses = isGaleriaPage 
    ? "bg-purple-900 text-white shadow-md sticky top-0 z-50" 
    : (transparent ? "transition-all duration-300 text-white" : "bg-gradient-to-r from-purple-900 to-purple-700 text-white shadow-md fixed top-0 left-0 w-full");

  return (
    <header className={`${navClasses} w-full font-lato`}>
      <nav className="container mx-auto px-4 py-3 flex flex-wrap items-center justify-between">
        
        {/* Mobile layout: Hamburger Menu | Logo | Text (in 2 lines) */}
        <div className="flex items-center justify-between w-full lg:w-auto">
          {/* Hamburger Icon (for mobile) - Left aligned on mobile */}
          <button
            onClick={toggleMenu}
            className="flex lg:hidden items-center px-3 py-2 border rounded text-white border-white/50 hover:text-white hover:border-white"
          >
            <svg className="fill-current h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><title>Menu</title><path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z"/></svg>
          </button>
          
          {/* Logo/Site Title - Center on mobile, left on desktop - Always visible now */}
          <Link to="/" onClick={scrollToTop} className="flex items-center space-x-2">
            <img 
              src="/img/logo2.png" 
              alt="Salon Fryzjerski u Małgosi Logo" 
              className="h-12 w-auto object-contain" 
            />
            <div className="font-playfair font-bold text-white">
              <div className="text-xl leading-tight">Salon Fryzjerski</div>
              <div className="text-xl leading-tight">u Małgosi</div>
            </div>
          </Link>
          
          {/* Empty div to balance the flex layout on mobile */}
          <div className="lg:hidden w-5"></div>
        </div>

        {/* Navigation Links (Desktop) - Right aligned */}
        <div className="hidden lg:flex items-center space-x-8 ml-auto">
          <div className="flex space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={scrollToTop}
                className={`text-white font-lato font-bold text-lg hover:underline decoration-2 underline-offset-4 transition-all duration-200 whitespace-nowrap relative py-1 ${activeLink === link.name ? 'underline' : ''}`}
                onMouseEnter={() => setHoverLink(link.name)}
                onMouseLeave={() => setHoverLink(null)}
              >
                {link.name}
              </Link>
            ))}
          </div>
          {/* Social links (desktop) */}
          <div className="flex space-x-4 ml-6">
            <a href="https://facebook.com/FryzjerkaMalgosia" aria-label="Facebook" className="hover:text-purple-200">
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
              </svg>
            </a>
            <a href="https://instagram.com" aria-label="Instagram" className="hover:text-purple-200">
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Navigation Links (Mobile) */}
        <div className={`w-full lg:hidden absolute top-full left-0 right-0 ${isOpen ? 'block' : 'hidden'} bg-purple-800 mt-0 py-4 px-4 z-10`}>
          <div className="flex flex-col space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-white font-lato font-bold text-lg hover:underline decoration-2 underline-offset-4 ${activeLink === link.name ? 'underline' : ''}`}
                onClick={() => {
                  setIsOpen(false);
                  setHoverLink(null);
                  scrollToTop();
                }}
              >
                {link.name}
              </Link>
            ))}
            {/* Social links (mobile) */}
            <div className="flex space-x-4 mt-4">
              <a href="https://facebook.com/FryzjerkaMalgosia" aria-label="Facebook" className="hover:text-purple-200">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                </svg>
              </a>
              <a href="https://instagram.com" aria-label="Instagram" className="hover:text-purple-200">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;